package org.matthewo.table

import emotion.css.ClassName
import emotion.react.css
import js.objects.buildReadonlyRecord
import org.matthewo.MATCH_SEARCH
import org.matthewo.TEAM_SEARCH
import org.matthewo.api.Schedule
import org.matthewo.api.forEachLevel
import org.matthewo.hooks.useCoroutineScope
import org.matthewo.hooks.useStatefulRef
import org.matthewo.pages.event.tabs.toCompareTeams
import org.matthewo.pages.event.tabs.toEventMatchResult
import org.matthewo.pages.team.tabs.EVENT_CODE_SEARCH
import org.matthewo.pages.team.tabs.toTeam
import org.matthewo.router.replaceAndNavigate
import org.matthewo.router.toExternal
import org.matthewo.styles.*
import react.*
import react.dom.html.ReactHTML.b
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.i
import react.dom.html.ReactHTML.span
import react.dom.html.ReactHTML.table
import react.dom.html.ReactHTML.tbody
import react.dom.html.ReactHTML.td
import react.dom.html.ReactHTML.thead
import react.dom.html.ReactHTML.tr
import tanstack.react.router.Link
import tanstack.react.router.useNavigate
import tanstack.react.router.useSearch
import web.cssom.*
import web.html.HTMLTableRowElement
import web.scroll.ScrollIntoViewOptions
import web.scroll.ScrollLogicalPosition
import web.scroll.center
import web.window.WindowTarget
import web.window._blank
import kotlin.js.Date
import kotlin.math.roundToInt

private val seasonTableWrapper = ClassName {
    width = 100.pct

    "thead td" {
        textAlign = TextAlign.center
        padding = HEADER_PADDING
        lineHeight = HEADER_LINE_HEIGHT
    }

    "tr, td" {
        margin = 0.px
    }
}
private val seasonTable = ClassName {
    width = 100.pct
    fontSize = 1.rem
    borderCollapse = BorderCollapse.collapse
    tableLayout = TableLayout.fixed
}
private val redHeader = ClassName {
    backgroundColor = TABLE_HEAD_RED
}
private val blueHeader = ClassName {
    backgroundColor = TABLE_HEAD_BLUE
}
private val allianceHeader = ClassName {
    minWidth = 20.em
    width = 50.pct
}
private val scoreHeader = ClassName {
    width = 6.em
}
private val matchLevelRow = ClassName {
    textAlign = TextAlign.center
    position = Position.sticky
    top = HEADER_LINE_HEIGHT + 2 * HEADER_PADDING
    backgroundColor = BACKGROUND_COLOR
    zIndex = integer(3)
    padding = 0.5.em
}

external interface MatchesTableProps : Props {
    var year: Int
    var code: String
    var matches: List<Schedule.Match>
    var team: Int?
}

val MatchesTable = FC<MatchesTableProps>("MatchesTable") { props ->
    val searchParams = useSearch()

    val selectedMatch = useMemo(props.matches) {
        if (null != props.team) null
        else searchParams["match"]?.toString()?.toIntOrNull() ?: props.matches.filter { null == it.scores }.minByOrNull { it.order }?.order
    }
    var selectedTeam by useState(props.team)
    var showProbability by useState(false)

    useEffect(props.team) {
        selectedTeam = props.team ?: searchParams[TEAM_SEARCH]?.toString()?.toIntOrNull()
    }

    div {
        css(seasonTableWrapper)
        table {
            css(seasonTable)
            thead {
                tr {
                    css {
                        position = Position.sticky
                        top = 0.px
                        zIndex = integer(4)
                    }
                    td {
                        css {
                            position = Position.sticky
                            left = 0.px
                            backgroundColor = BACKGROUND_COLOR
                            width = 4.em
                        }
                        span {
                            +"Match"
                        }
                    }
                    td {
                        css(scoreHeader) {
                            backgroundColor = BACKGROUND_COLOR
                        }
                        colSpan = 2
                        span {
                            css {
                                textDecoration = TextDecoration.underline
                            }
                            +"Result"
                        }
                        onClick = {
                            showProbability = !showProbability
                        }
                    }
                    td {
                        css(allianceHeader, redHeader)
                        span {
                            +"Red"
                        }
                    }
                    td {
                        css(allianceHeader, blueHeader)
                        span {
                            +"Blue"
                        }
                    }
                }
            }
            tbody {
                props.matches.forEachLevel { level, matches, multipleSeries, multipleNumbers ->
                    MatchGroup {
                        this.year = props.year
                        this.code = props.code
                        this.team = props.team
                        this.level = level.title
                        this.matches = matches.sortedBy { it.order }
                        this.showSeries = multipleSeries
                        this.showNumber = multipleNumbers
                        this.selectedMatch = selectedMatch
                        this.selectedTeam = selectedTeam
                        this.showProbability = showProbability
                        this.select = { _, team ->
                            selectedTeam = team
                        }
                    }
                }
            }
        }
    }
}

private val matchTeamsCell = ClassName {
    padding = 0.px
}
private val matchTeamWrapper = ClassName {
    width = 100.pct
    height = 100.pct
    display = Display.grid
    gridAutoFlow = GridAutoFlow.column
    gridAutoColumns = 1.fr
}

private external interface MatchGroupProps : Props {
    var year: Int
    var code: String
    var team: Int?
    var level: String
    var matches: List<Schedule.Match>
    var showSeries: Boolean
    var showNumber: Boolean
    var selectedMatch: Int?
    var selectedTeam: Int?
    var select: (Schedule.Match, Int) -> Unit
    var showProbability: Boolean
}

private val MatchGroup = FC<MatchGroupProps>("MatchGroup") { props ->
    val coroutineScope = useCoroutineScope()
    val navigate = useNavigate()

    var (rowRef, rowRefCallback) = useStatefulRef<HTMLTableRowElement>()

    useEffect(props.selectedMatch, rowRef) {
        rowRef?.scrollIntoView(ScrollIntoViewOptions(block = ScrollLogicalPosition.center))
    }

    tr {
        td {
            colSpan = 7
            css(matchLevelRow)
            b {
                +props.level
            }
        }
    }
    props.matches.forEachIndexed { i, match ->
        tr {
            if (match.order == props.selectedMatch) {
                ref = rowRefCallback
            }

            val bright = i % 2 == 0
            td {
                css {
                    position = Position.sticky
                    left = 0.px
                    backgroundColor = BACKGROUND_COLOR.lighten(if (bright) 1.5 else 1.0)
                    zIndex = integer(2)
                    padding = 0.5.em
                }
                div {
                    css {
                        display = Display.flex
                        flexDirection = FlexDirection.row
                        alignItems = AlignItems.center
                        gap = 0.5.em
                    }
                    val video = match.video
                    if (null != video) {
                        Link {
                            css {
                                height = 2.0.em
                                alignContent = AlignContent.center
                                color = FONT_COLOR
                                textDecoration = None.none
                            }
                            coroutineScope.replaceAndNavigate(navigate, {
                                search = buildReadonlyRecord {
                                    if (null == props.team) this[MATCH_SEARCH] = match.order
                                    else this[EVENT_CODE_SEARCH] = props.code
                                }
                            }) {
                                toExternal(video)
                            }
                            target = WindowTarget._blank
                            +"▶  "
                        }
                    }
                    Link {
                        css {
                            height = 2.0.em
                            alignContent = AlignContent.center
                            flexGrow = number(1.0)
                            color = FONT_COLOR
                        }

                        +when {
                            !props.showSeries && props.showNumber -> "${match.number}"
                            props.showSeries && !props.showNumber -> "${match.series}"
                            else -> "${match.series}-${match.number}"
                        }

                        coroutineScope.replaceAndNavigate(navigate, {
                            search = buildReadonlyRecord {
                                if (null == props.team) this[MATCH_SEARCH] = match.order
                                else this[EVENT_CODE_SEARCH] = props.code
                            }
                        }) {
                            toCompareTeams(eventCode = props.code, match = match)
                        }
                    }
                }
            }

            val scores = match.scores
            when {
                null != scores -> MatchScore(scores.red to scores.redRP, scores.blue to scores.blueRP, { it.first }, bright) { score ->
                    Link {
                        css {
                            color = FONT_COLOR
                            display = Display.grid
                            gridTemplateRows = array(0.6.em, 1.fr, 0.6.em)
                            alignItems = AlignItems.start
                        }

                        coroutineScope.replaceAndNavigate(navigate, {
                            search = buildReadonlyRecord {
                                if (null == props.team) this[MATCH_SEARCH] = match.order
                                else this[EVENT_CODE_SEARCH] = props.code
                            }
                        }) {
                            toEventMatchResult(eventCode = props.code, match = match)
                        }

                        val rp = score.second
                        if (null != rp) {
                            div {
                                css {
                                    position = Position.relative
                                    display = Display.flex
                                    flexDirection = FlexDirection.row
                                    alignItems = AlignItems.center
                                    justifyContent = JustifyContent.spaceEvenly
                                }
                                rp.forEach {
                                    div {
                                        css {
                                            height = 0.25.em
                                            width = 0.25.em
                                            aspectRatio = number(1.0)
                                            borderRadius = 50.pct
                                            backgroundColor = if (it) FONT_COLOR else Globals.unset
                                        }
                                    }
                                }
                            }
                        }

                        +"${score.first}"
                    }
                }

                props.showProbability -> MatchScore(
                    match.redWinProbability * 100.0,
                    (1 - match.redWinProbability) * 100.0,
                    { it },
                    bright
                ) { score ->
                    +"${score.roundToInt()}%"
                }

                else -> {
                    td {
                        css {
                            backgroundColor = BACKGROUND_COLOR.lighten(if (bright) 1.5 else 1.0)
                            padding = 0.5.em
                            textAlign = TextAlign.center
                        }
                        colSpan = 2
                        val time = (match.played ?: match.scheduled)?.let { Date(it).toLocaleTimeString(options = DATE_FORMAT) }
                        +(time ?: "")
                    }
                }
            }

            val displayAlliances = [match.red to AllianceColor.RED, match.blue to AllianceColor.BLUE]
            displayAlliances.forEach { (alliance, color) ->
                td {
                    css(matchTeamsCell)
                    div {
                        css(matchTeamWrapper)
                        alliance.teams.forEach { team ->
                            MatchTeam {
                                allianceColor = color
                                number = team.number
                                name = team.name
                                onField = team.onField
                                surrogate = team.surrogate
                                dq = team.dq
                                selected = props.selectedTeam == team.number
                                onHighlight = {
                                    navigate {
                                        search = buildReadonlyRecord {
                                            if (null == props.team) {
                                                this[MATCH_SEARCH] = match.order.toString()
                                                this[TEAM_SEARCH] = team.number.toString()
                                            } else {
                                                this[EVENT_CODE_SEARCH] = props.code
                                            }
                                        }
                                        replace = true
                                    }
                                    props.select(match, team.number)
                                }
                                onSelect = {
                                    navigate {
                                        toTeam(teamNumber = team.number, eventCode = props.code)
                                    }
                                }
                                this.bright = bright
                            }
                        }
                    }
                }
            }
        }
    }
}


private external interface MatchScoreProps : PropsWithChildren {
    var bright: Boolean
    var win: Boolean
    var allianceColor: AllianceColor
}

private val MatchScore = FC<MatchScoreProps>("MatchScore") { props ->
    td {
        css {
            textAlign = TextAlign.center
            backgroundColor = (if (props.win) props.allianceColor.backgroundColor else BACKGROUND_COLOR).lighten(if (props.bright) 1.5 else 1.0)
        }
        +props.children
    }
}

private fun <T> ChildrenBuilder.MatchScore(red: T, blue: T, value: (T) -> Number, bright: Boolean, display: ChildrenBuilder.(score: T) -> Unit) {
    val redScore = value(red).toDouble()
    val blueScore = value(blue).toDouble()
    [
        Triple(red, redScore > blueScore, AllianceColor.RED),
        Triple(blue, blueScore > redScore, AllianceColor.BLUE),
    ].forEach { (team, win, allianceColor) ->
        MatchScore {
            this.bright = bright
            this.win = win
            this.allianceColor = allianceColor
            display(team)
        }
    }
}


private external interface MatchTeamProps : Props {
    var number: Int
    var name: String
    var onField: Boolean
    var surrogate: Boolean
    var dq: Boolean
    var selected: Boolean
    var onHighlight: () -> Unit
    var onSelect: () -> Unit
    var allianceColor: AllianceColor
    var bright: Boolean
}

private val MatchTeam = FC<MatchTeamProps>("MatchTeam") { props ->
    div {
        css {
            padding = 0.5.em
            minWidth = 0.px
            backgroundColor = if (props.selected) {
                props.allianceColor.highlightColor
            } else {
                props.allianceColor.backgroundColor.lighten(if (props.bright) 1.5 else 1.0)
            }
            color = if (props.onField) FONT_COLOR else NamedColor.gray
        }
        onClick = {
            props.onHighlight()
        }
        onDoubleClick = {
            props.onSelect()
        }
        div {
            css {
                overflow = Overflow.hidden
                whiteSpace = WhiteSpace.nowrap
                if (props.dq) {
                    textDecoration = TextDecoration.lineThrough
                    textDecorationThickness = 0.15.em
                }
            }
            +"${props.number}${if (props.surrogate) "*" else ""}"
        }
        div {
            css {
                overflow = Overflow.hidden
                whiteSpace = WhiteSpace.nowrap
            }
            i {
                +props.name
            }
        }
    }
}
